import React, {useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {classes, style} from './video-player.st.css';
import {useDynamicRef, useDynamicRefCurrentInstance} from "../dynamic-ref";
import {
    asyncValueGetResolvedValueOrDefault,
    AsyncValueStates,
    asyncWaitForValue,
    Evaluable,
    evaluateWhenFunction,
    EventEmitter,
    EventReference,
    IDisposable,
    Promisable,
    Values
} from "@wix/devzai-utils-common";
import {useAsyncValue, useRenderState} from "../hooks";
import {useRootWindow} from "../root-window-context/root-window-context";
import {domEventOnceOneOfEventsEmitted, domLoadScript} from "@wix/devzai-utils-dom";
import {gridLayoutStretchAllChildren} from "../grid-layout/grid-layout";

export const VideoPlayerMediaStreamTypes = {
    MP4: 'MP4',
    HLS: 'HLS'
} as const;

export type VideoPlayerMediaStreamTypes = Values<typeof VideoPlayerMediaStreamTypes>;

export const VideoPlayerAutoplayFailureBehaviour = {
    None: 'None',
    PlayMuted: 'PlayMuted',
    PlayOnInteraction: 'PlayOnInteraction'
} as const;

export type VideoPlayerAutoplayFailureBehaviour = Values<typeof VideoPlayerAutoplayFailureBehaviour>;

export type VideoPlayerEvents = {
    eventStartedPlaying: void;
    eventVideoEnded: void;
    eventProgress: {currentTime: number; duration: number};
}

// https://github.com/wix-incubator/playable/blob/02788b00a0ea837f70bc10c6dcd105a40b6d8423/src/constants/events/video.ts#L3
export const PlayablePlayerEvents = {
    StateChanged: 'video-events/state-changed',
    PlayRequest: 'video-events/play-request',
    Error: 'video-events/error',
    PlayAborted: 'video-events/play-aborted',
    CurrentTimeUpdated: 'video-events/current-time-updated'
} as const;

export type PlayablePlayerEvents = Values<typeof PlayablePlayerEvents>;

export const PlayablePlayerState = {
    Playing: 'engine-state/playing',
    SrcSet: 'engine-state/src-set',
    LoadStarted: 'engine-state/load-started',
    MetadataLoaded: 'engine-state/metadata-loaded',
    ReadyToPlay: 'engine-state/ready-to-play',
    SeekInProgress: 'engine-state/seek-in-progress',
    PlayRequested: 'engine-state/play-requested',
    Waiting: 'engine-state/waiting',
    Paused: 'engine-state/paused',
    Ended: 'engine-state/ended',
} as const;

export type PlayablePlayerState = Values<typeof PlayablePlayerState>;

export const VideoPlayerPreload = {
    None: 'none',
    Metadata: 'metadata',
    Auto: 'auto'
} as const;

export type VideoPlayerPreload = Values<typeof VideoPlayerPreload>;

type VideoPlayerEventCallbacks = {
    [PlayablePlayerEvents.StateChanged]: (stateInfo: {prevState: null | PlayablePlayerState; nextState: PlayablePlayerState}) => void;
    [PlayablePlayerEvents.PlayRequest]: () => void;
    [PlayablePlayerEvents.PlayAborted]: () => void;
    [PlayablePlayerEvents.CurrentTimeUpdated]: () => void;
    [PlayablePlayerEvents.Error]: (arg: any) => void;
}

interface PlayablePlayer {
    mute: () => void;
    unmute: () => void;
    play: () => void;
    setAutoplay: (autoplay: boolean) => void;
    getPlaybackState: () => any;
    isPaused: () => boolean;
    isEnded: () => boolean;
    getDuration: () => number;
    getCurrentTime: () => number;
    destroy: () => void;
    attachToElement: (element: HTMLElement) => void;
    setSrc: (src: VideoPlayer.VideoUrlSource[]) => void;
    on: <E extends PlayablePlayerEvents>(event: E, callback: VideoPlayerEventCallbacks[E]) => void;
    off: (event: PlayablePlayerEvents) => void;
}

type PlayablePlayerConfig = {
    src: string;
    muted?: boolean;
    autoplay?: boolean;
    loop: boolean;
    poster?: string;
    playsinline: boolean;
    hideOverlay: boolean;
    preload?: 'auto' | 'metadata' | 'none';
}

type Playable = {
    create: (options: PlayablePlayerConfig) => PlayablePlayer;
}

export const VideoPlayer = React.memo(React.forwardRef(function VideoPlayer (props: VideoPlayer.Props, ref: React.Ref<VideoPlayer>) {
    const {
        src,
        autoplay = false,
        muted = false,
        loop = false,
        preload = VideoPlayerPreload.Metadata,
        poster,
        errorView,
        autoplayFailureBehaviour = VideoPlayerAutoplayFailureBehaviour.None,

        className,
        ...htmlAttributes
    } = props;

    const playerElementRef = useDynamicRef<HTMLDivElement>();
    const rootElement = useDynamicRefCurrentInstance(playerElementRef);
    const rootWindow = useRootWindow();

    const playablePlayerRef = useDynamicRef<PlayablePlayer | null>();
    const playablePlayer = useDynamicRefCurrentInstance(playablePlayerRef);


    const videoPlayerEventEmitter = useMemo(() => {
        return new EventEmitter<VideoPlayerEvents>();
    }, [])

    const [duration, setDuration] = useState<null | number>(null);

    const renderState = useRenderState({
        autoplayFailureBehaviour: autoplayFailureBehaviour,
        videoPlayerEventEmitter: videoPlayerEventEmitter,
        duration: duration,
    })

    const asyncPlayable = useAsyncValue(async () => {

        // Docs: https://wix-incubator.github.io/playable/
        await domLoadScript('https://unpkg.com/playable/dist/statics/playable-hls.bundle.min.js', {
            skipIfAlreadyLoaded: true,
            rootWindow: rootWindow
        });

        return await asyncWaitForValue(async (skip) => {
            return ((rootWindow ?? window) as {
                Playable?: Playable;
            }).Playable ?? skip;
        }, {
            interval: 100
        })
    }, [rootWindow]);

    const asyncSrc = useAsyncValue(async () => evaluateWhenFunction(src), [src]);

    const Playable = asyncValueGetResolvedValueOrDefault(asyncPlayable, undefined);
    const resolvedSrc = asyncValueGetResolvedValueOrDefault(asyncSrc, undefined);
    const [showPoster, setShowPoster] = useState(true);
    const [error, setError] = useState<unknown | null>(null);

    const videoPlayerController = useMemo<VideoPlayer>(() => {

        return {
            play() {
                if (playablePlayerRef.current) {
                    playablePlayerRef.current.play();
                } else if (error) {
                    setShowPoster(false);
                }
            },
            getDuration() {
                return renderState.current.duration;
            },
            getCurrentTime() {
                return playablePlayerRef.current?.getCurrentTime() ?? null;
            },
            eventStartedPlaying: videoPlayerEventEmitter.createEventReference('eventStartedPlaying'),
            eventVideoEnded: videoPlayerEventEmitter.createEventReference('eventVideoEnded'),
            eventProgress: videoPlayerEventEmitter.createEventReference('eventProgress'),
        }
    }, [error, videoPlayerEventEmitter, renderState]);

    const userGestureEventBindingRef = useRef<IDisposable>();

    useLayoutEffect(() => {
        if (asyncSrc.state === AsyncValueStates.Rejected) {
            setError(asyncSrc.reason);
        }
    }, [asyncSrc.state]);

    useImperativeHandle(ref, () => videoPlayerController, [videoPlayerController]);

    useLayoutEffect(() => {

        if (rootElement && Playable && resolvedSrc) {

            const player = playablePlayerRef.current = Playable.create({
                src: resolvedSrc as any,
                muted: muted,
                loop: loop,
                playsinline: true,
                preload: preload,
                autoplay: autoplay,
                hideOverlay: true // Hides the play button with the poster.
            });

            player.attachToElement?.(rootElement);

            player.on(PlayablePlayerEvents.StateChanged, state => {
                if (state.nextState === PlayablePlayerState.MetadataLoaded) {
                    setDuration(player.getDuration());
                }

                if (state.nextState === PlayablePlayerState.Playing) {
                    setShowPoster(false);

                    renderState.current.videoPlayerEventEmitter.emit('eventStartedPlaying');
                }

                if (state.nextState === PlayablePlayerState.Ended) {
                    renderState.current.videoPlayerEventEmitter.emit('eventVideoEnded');
                }
            });

            player.on(PlayablePlayerEvents.Error, arg => {
                setError(arg);
            });

            player.on(PlayablePlayerEvents.CurrentTimeUpdated, () => {
                renderState.current.videoPlayerEventEmitter.emit('eventProgress', {
                    currentTime: player.getCurrentTime(),
                    duration: player.getDuration()
                });
            });

            player.on(PlayablePlayerEvents.PlayAborted, () => {

                switch (renderState.current.autoplayFailureBehaviour) {
                    case VideoPlayerAutoplayFailureBehaviour.PlayMuted: {
                        player.mute();
                        player.play();
                        break;
                    }
                    case VideoPlayerAutoplayFailureBehaviour.PlayOnInteraction: {
                        userGestureEventBindingRef.current = domEventOnceOneOfEventsEmitted(
                            document,
                            ['mousedown', 'keydown', 'pointerdown', 'pointerup', 'touchend', 'touchdown'],
                            () => {
                                videoPlayerController.play()
                            }
                        )
                        break;
                    }
                    case VideoPlayerAutoplayFailureBehaviour.None: {
                        console.log(`Play aborted`);
                        break;
                    }
                }
            })

            return () => {
                playablePlayerRef.current = null;

                player.destroy();
                setShowPoster(true);
                setDuration(null);
                setError(null);
                userGestureEventBindingRef.current?.dispose();
            }
        }

        return undefined;
    }, [rootElement, resolvedSrc, Playable, muted, autoplay, loop, preload, renderState, videoPlayerController]);

    useLayoutEffect(() => {
        if (autoplay && playablePlayer) {
            videoPlayerController.play();
        }
    }, [autoplay, playablePlayer]);

    const overlayRenderProps = {
        duration: duration
    } satisfies VideoPlayer.OverlayRenderProps;

    const evaluatedPoster = showPoster ?
        evaluateWhenFunction(poster, overlayRenderProps) : null;

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, className, gridLayoutStretchAllChildren())}
        >
            <div
                className={style(classes.playerElement, {})}
                ref={playerElementRef}
            />
            {!showPoster && error ? (
                <div
                    className={style(classes.errorView, gridLayoutStretchAllChildren())}
                >
                    {evaluateWhenFunction(errorView, overlayRenderProps)}
                </div>
            ) : null}
            {evaluatedPoster ? (
                <div
                    className={style(classes.overlay, gridLayoutStretchAllChildren())}
                    onClick={() => {
                        videoPlayerController.play();
                    }}
                >
                    {evaluatedPoster}
                </div>
            ) : null}
        </div>
    );
}));

export interface VideoPlayer {
    eventStartedPlaying: EventReference<VideoPlayerEvents, "eventStartedPlaying">
    eventVideoEnded: EventReference<VideoPlayerEvents, "eventVideoEnded">
    eventProgress: EventReference<VideoPlayerEvents, "eventProgress">
    play () : void;
    getDuration () : number | null;
    getCurrentTime () : number | null;
}

export namespace VideoPlayer {

    export type VideoUrlSource = {
        url: string;
        type?: VideoPlayerMediaStreamTypes;
    }

    export type VideoSource = VideoUrlSource[];

    export type OverlayRenderProps = {
        duration: number | null;
    }

    export interface Props extends React.HTMLAttributes<any> {
        src: Evaluable<() => Promisable<VideoSource>>;
        autoplay?: boolean;
        muted?: boolean;
        loop?: boolean;
        preload?: VideoPlayerPreload;
        poster?: Evaluable<(renderProps: OverlayRenderProps) => React.ReactNode>;
        errorView?: Evaluable<(renderProps: OverlayRenderProps) => React.ReactNode>;
        autoplayFailureBehaviour?: VideoPlayerAutoplayFailureBehaviour;
    }
}
